// Fragen mit Zusammenhang im CRM (E3, S6; CLAUDE.md, Grundsatz 9: „Fragen
// stellt man im System, an dem Ding, um das es geht“) – die reine Logik,
// ohne React, damit die Tests sie ohne Browser prüfen:
//   - die Fragen einer Besichtigung nach dem Stück ordnen, an dem sie hängen
//     (Anlage, Besichtigung, Station, Foto, Notiz – der Bezug wie am Server);
//   - woran eine Frage hängt, in Worten: „Station 3 · am Foto“, „zur Anlage“;
//   - zählen, was offen ist, und das Band dazu: „3 offene Fragen – ansehen
//     und antworten“ (Gernots Entscheidung 2: kein Web-Push, dafür oben,
//     sichtbar, nicht zu übersehen);
//   - sortieren: offene älteste zuerst (wer am längsten wartet, steht
//     oben), erledigte zuletzt erledigt zuerst;
//   - der Text, wie er hinausgeht, und die Kennung fürs Senden.
//
// Dieselben Gedanken hat die Doku-App (apps/doku/src/gespraech.ts) – die
// Apps teilen keinen Code untereinander, nur über packages/ (anzeige.ts).

import { BUERO_GRENZEN, type FrageAmServer, type FrageBezug, type FrageMitKontext, type NutzerKurz } from "@happy-energy/kern";

/** Woran eine Frage hängt – Tabelle und Kennung, wie am Server. */
export interface Bezug {
  tabelle: FrageBezug;
  id: string;
}

/** Ein Schlüssel je Stück: „foto:<kennung>“. */
export function bezugSchluessel(b: Bezug): string {
  return `${b.tabelle}:${b.id}`;
}

function zeit(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

type Sortierbar = Pick<FrageAmServer, "id" | "gestelltAm">;

/** Älteste zuerst – wer am längsten wartet, steht oben. Gleich alt: nach Kennung, damit nichts springt. */
export function aeltesteZuerst<F extends Sortierbar>(fragen: readonly F[]): F[] {
  return [...fragen].sort((x, y) => zeit(x.gestelltAm) - zeit(y.gestelltAm) || x.id.localeCompare(y.id));
}

/**
 * Die Fragen nach dem Stück, an dem sie hängen – je Stück älteste zuerst,
 * so liest sich ein Faden von oben nach unten.
 */
export function nachBezug<F extends Sortierbar & Pick<FrageAmServer, "bezug">>(fragen: readonly F[]): Map<string, F[]> {
  const gruppen = new Map<string, F[]>();
  for (const f of aeltesteZuerst(fragen)) {
    const schluessel = bezugSchluessel(f.bezug);
    const liste = gruppen.get(schluessel);
    if (liste) liste.push(f);
    else gruppen.set(schluessel, [f]);
  }
  return gruppen;
}

/** Die Fragen einer Besichtigung, geordnet nach dem Stück (`nachBezug`). */
export type FragenJeStueck<F = FrageAmServer> = ReadonlyMap<string, readonly F[]>;

/** Die Fragen an einem Stück – keine: eine leere Liste. */
export function fragenAn<F>(gruppen: FragenJeStueck<F>, bezug: Bezug): readonly F[] {
  return gruppen.get(bezugSchluessel(bezug)) ?? [];
}

/** Wie viele davon offen sind. */
export function offenZahl(fragen: readonly Pick<FrageAmServer, "stand">[]): number {
  return fragen.filter((f) => f.stand === "offen").length;
}

/** „1 offene Frage“, „3 offene Fragen“ – `eins` und `mehr` mit # für die Zahl. */
export function anzahl(n: number, eins: string, mehr: string): string {
  return (n === 1 ? eins : mehr).replace("#", String(n));
}

// --- Woran eine Frage hängt, in Worten ----------------------------------------------------

/**
 * Für Listen (Seite „Fragen“, Akte): „Station 3“, „Station 3 · am Foto“,
 * „Station 3 · an einer Notiz“, „zur Besichtigung“, „zur Anlage“. Ohne
 * Nummer (das Stück ist nicht mehr da): nur die Art.
 */
export function ortText(tabelle: FrageBezug, stationNr: number | null): string {
  const station = stationNr === null ? null : `Station ${stationNr}`;
  switch (tabelle) {
    case "anlage":
      return "zur Anlage";
    case "aufnahme":
      return "zur Besichtigung";
    case "station":
      return station ?? "an einer Station";
    case "foto":
      return station ? `${station} · am Foto` : "am Foto";
    case "notiz":
      return station ? `${station} · an einer Notiz` : "an einer Notiz";
  }
}

/** Die Zeile über einer Frage in der Liste: „Familie Maier · Graz · Station 3 · am Foto“. */
export function zusammenhangText(f: Pick<FrageMitKontext, "bezug" | "kontext">): string {
  return [f.kontext.kundeName, f.kontext.ort, ortText(f.bezug.tabelle, f.kontext.stationNr)].join(" · ");
}

/** Was eine Besichtigung von ihren Stücken braucht, um die Nummer der Station zu finden. */
export interface Stuecke {
  stationen: readonly { id: string; nr: number }[];
  fotos: readonly { id: string; stationId: string }[];
  notizen: readonly { id: string; stationId: string | null; fotoId: string | null }[];
}

/**
 * Die Nummer der Station, an der ein Stück hängt – die Station selbst, die
 * Station des Fotos, die der Notiz (direkt oder über ihr Foto); null bei
 * Anlage und Besichtigung. Dieselbe Regel wie am Server (fragen.ts,
 * stationNrDesBezugs) – hier aus der Besichtigung, die schon geladen ist.
 */
export function stationNrAn(bezug: Bezug, s: Stuecke): number | null {
  const nrDer = (stationId: string | null | undefined) => (stationId ? (s.stationen.find((x) => x.id === stationId)?.nr ?? null) : null);
  switch (bezug.tabelle) {
    case "station":
      return nrDer(bezug.id);
    case "foto":
      return nrDer(s.fotos.find((f) => f.id === bezug.id)?.stationId);
    case "notiz": {
      const n = s.notizen.find((x) => x.id === bezug.id);
      if (!n) return null;
      return nrDer(n.stationId ?? s.fotos.find((f) => f.id === n.fotoId)?.stationId);
    }
    default:
      return null;
  }
}

/**
 * Das Foto, in dessen großer Ansicht eine Frage steht: die Frage am Foto
 * selbst oder an einer Notiz zum Foto. Sonst null – sie steht auf der Seite.
 */
export function fotoDerFrage(bezug: Bezug, notizen: Stuecke["notizen"]): string | null {
  if (bezug.tabelle === "foto") return bezug.id;
  if (bezug.tabelle === "notiz") return notizen.find((n) => n.id === bezug.id)?.fotoId ?? null;
  return null;
}

/** Die Marke an einem Stück: „1 offene Frage“, „3 offene Fragen“. */
export function offenText(n: number): string {
  return anzahl(n, "1 offene Frage", "# offene Fragen");
}

/**
 * Die Marke auf einer Foto-Kachel – die Fragen am Foto und an seinen
 * Notizen (alles, was die große Ansicht zeigt): Ist etwas offen, zählt nur
 * das („1 offene Frage“, in Lime); sonst still, wie viele es gab („2
 * Fragen“). `offen`: wie viele offen sind. Keine Fragen: null.
 */
export function kachelZaehler(fragen: readonly Pick<FrageAmServer, "stand">[]): { text: string; offen: number } | null {
  if (fragen.length === 0) return null;
  const offen = offenZahl(fragen);
  return { text: offen > 0 ? offenText(offen) : anzahl(fragen.length, "1 Frage", "# Fragen"), offen };
}

// --- Listen ----------------------------------------------------------------------------------

/** Für die Akte: offene zuerst (älteste zuerst), dann die erledigten (zuletzt erledigt zuerst). */
export function offeneZuerst<F extends Sortierbar & Pick<FrageAmServer, "stand" | "erledigtAm">>(fragen: readonly F[]): F[] {
  const offen = aeltesteZuerst(fragen.filter((f) => f.stand === "offen"));
  const erledigt = fragen
    .filter((f) => f.stand !== "offen")
    .sort((x, y) => zeit(y.erledigtAm ?? y.gestelltAm) - zeit(x.erledigtAm ?? x.gestelltAm) || x.id.localeCompare(y.id));
  return [...offen, ...erledigt];
}

/** So weit zurück zeigt die Seite „Fragen“ die erledigten. */
export const ERLEDIGT_TAGE = 30;

/** Die erledigten der letzten Zeit – zuletzt erledigt zuerst. */
export function erledigteDerLetztenZeit<F extends Pick<FrageAmServer, "id" | "stand" | "erledigtAm">>(fragen: readonly F[], jetzt: Date, tage = ERLEDIGT_TAGE): F[] {
  const grenze = jetzt.getTime() - tage * 86_400_000;
  return fragen
    .filter((f) => f.stand === "erledigt" && f.erledigtAm !== null && zeit(f.erledigtAm) >= grenze)
    .sort((x, y) => zeit(y.erledigtAm ?? "") - zeit(x.erledigtAm ?? "") || x.id.localeCompare(y.id));
}

// --- Das Band --------------------------------------------------------------------------------

/**
 * Die offenen Fragen, die eine Seite NICHT selbst zeigt – die Freigabe und
 * die Akte zeigen die ihrer Besichtigungen mit eigenem Band; das Band oben
 * nennt dann nur die übrigen („1 weitere …“).
 */
export function ausserHier<F extends Pick<FrageAmServer, "aufnahmeId">>(fragen: readonly F[], hier: readonly string[] | undefined): F[] {
  if (!hier || hier.length === 0) return [...fragen];
  return fragen.filter((f) => !hier.includes(f.aufnahmeId));
}

/** „3 offene Fragen – ansehen und antworten“; „weitere“, wenn die Seite selbst schon Fragen zeigt. */
export function bandText(zahl: number, weitere: boolean): string {
  const wort = weitere ? anzahl(zahl, "1 weitere offene Frage", "# weitere offene Fragen") : anzahl(zahl, "1 offene Frage", "# offene Fragen");
  return `${wort} – ansehen und antworten`;
}

// --- Schreiben -----------------------------------------------------------------------------

/** Wer: „Du“ für mich, sonst der Name, wie ihn die Leute im Büro kennen. */
export function werText(von: NutzerKurz, ichId: string): string {
  return von.id === ichId ? "Du" : von.name;
}

/** Der Text, wie er hinausgeht: getrimmt – null, wenn leer oder zu lang (dieselbe Grenze wie im Vertrag). */
export function gespraechText(text: string): string | null {
  const t = text.trim();
  return t !== "" && t.length <= BUERO_GRENZEN.frageText ? t : null;
}

/** Ein Senden: welche Kennung mit welchem Text. */
export interface Versuch {
  id: string;
  text: string;
}

/**
 * Die Kennung fürs Senden: dieselbe wie beim letzten Versuch, solange der
 * Text derselbe ist – ging die Antwort im Funkloch verloren und war doch
 * schon am Server, legt ein zweites Senden nichts doppelt an (der Server
 * vergleicht und sagt „ok“). Ein geänderter Text ist eine neue Frage oder
 * Antwort und bekommt eine neue Kennung.
 */
export function kennungFuer(letzter: Versuch | null, text: string, neu: () => string): Versuch {
  return letzter !== null && letzter.text === text ? letzter : { id: neu(), text };
}
